import { createSteeringURL } from '@/api'
import { useI18n } from '@/i18n'
import { steeringAuthInit, useOnAuthBehaviour } from '@/stores/auth'
import { License } from '@/types'
import { APIConsume, useRetrieveHelpers } from '@/util'
import { APIListPaginated, APIRetrieve, createExtendableAPI } from '@rokoli/bnb/drf'
import { defineStore } from 'pinia'
import { computed, MaybeRefOrGetter, toRef } from 'vue'

type LicensedWork = {
  credits: string
  isUseExplicitlyGrantedByAuthor: boolean
}

export function useLicenseValidator(
  license: MaybeRefOrGetter<License | null>,
  licensedWork: MaybeRefOrGetter<LicensedWork>,
) {
  const _license = toRef(license)
  const _licensedWork = toRef(licensedWork)
  const { t } = useI18n()
  const isUseExplicitlyGrantedByAuthorError = computed(() => {
    if (
      _license?.value?.requiresExpressPermissionForPublication &&
      !_licensedWork.value.isUseExplicitlyGrantedByAuthor
    ) {
      return { message: t('license.validation.requiresExpressPermissionForPublication') }
    }

    return undefined
  })
  const needsAuthorError = computed(() => {
    if (_license?.value?.needsAuthor && !_licensedWork.value.credits.trim()) {
      return { message: t('license.validation.requiresCredits') }
    }
    return undefined
  })
  const hasError = computed(
    () => isUseExplicitlyGrantedByAuthorError.value || needsAuthorError.value,
  )
  return { isUseExplicitlyGrantedByAuthorError, needsAuthorError, hasError }
}

export async function findLicenseByIdentifier(identifier: string) {
  const matchIdentifier = identifier.toLowerCase().trim()
  const licenseStore = useLicenseStore()
  await licenseStore.ready
  const license = licenseStore.items.find(
    // There is no official mention, but the consensus seems to be
    // that SPDX identifiers are case-insensitive for matching purposes.
    (license) => license.identifier.toLowerCase() === matchIdentifier,
  )
  return license ?? null
}

export const useLicenseStore = defineStore('licenses', () => {
  const endpoint = createSteeringURL.prefix('licenses')
  const { api, base } = createExtendableAPI<License>(endpoint, steeringAuthInit)
  const listOperations = APIListPaginated(api)
  const consumeOperations = APIConsume(listOperations.listIsolated)
  const retrieveOperations = APIRetrieve(api)

  const { done } = useOnAuthBehaviour(() => consumeOperations.consume(), { once: true })

  return {
    ready: done,
    ...base,
    ...listOperations,
    ...consumeOperations,
    ...retrieveOperations,
    ...useRetrieveHelpers({ ...base, ...retrieveOperations }),
  }
})
